import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cacheKeys, useFetcher } from '../../graphql';

export interface SetWorkspaceEmailAutoResponderTextInput {
  workspaceId: string;
  emailAutoResponderText: string;
}

export interface SetWorkspaceEmailAutoResponderTextResult {
  workspaceId: string;
  emailAutoResponderText: string | null;
}

interface SetWorkspaceEmailAutoResponderTextResponse {
  workspace: {
    setWorkspaceEmailAutoResponderText: SetWorkspaceEmailAutoResponderTextResult;
  };
}

/**
 * Set (or clear, with an empty string) the workspace's autoresponder text —
 * Communication settings, plan 0024 addendum. Requires global admin on the
 * backend (`*.*`), same gate as `useSetWorkspaceEmailEnabled`.
 */
export const useSetWorkspaceEmailAutoResponderText = () => {
  const queryClient = useQueryClient();
  const fetcherFn = useFetcher<
    SetWorkspaceEmailAutoResponderTextResponse,
    { input: SetWorkspaceEmailAutoResponderTextInput }
  >(SET_WORKSPACE_EMAIL_AUTO_RESPONDER_TEXT);

  return useMutation({
    mutationFn: async (input: SetWorkspaceEmailAutoResponderTextInput) => {
      const data = await fetcherFn({ input });
      return data.workspace.setWorkspaceEmailAutoResponderText;
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({
        queryKey: cacheKeys('workspace').detail(result.workspaceId),
      });
    },
  });
};

const SET_WORKSPACE_EMAIL_AUTO_RESPONDER_TEXT = `
  mutation SetWorkspaceEmailAutoResponderText($input: SetWorkspaceEmailAutoResponderTextInput!) {
    workspace {
      setWorkspaceEmailAutoResponderText(input: $input) {
        workspaceId
        emailAutoResponderText
      }
    }
  }
`;
